import React, { useState } from 'react';
import { Filter, X, Calendar, Tag, User, AlertCircle, Search } from 'lucide-react';

export interface HistoryFilter {
  timeRange: string;
  categories: string[];
  providers: string[];
  severity: string[];
  searchTerm: string;
}

export type FilterCategory = 'conditions' | 'allergies' | 'surgeries' | 'medications' | 'immunizations' | 'lab-results' | 'vitals' | 'documents';

interface HistoryFiltersProps {
  onFilterChange: (filters: HistoryFilter) => void;
  availableCategories: Array<{ id: FilterCategory; label: string }>;
  availableProviders: Array<{ id: string; name: string }>;
  className?: string;
}

/**
 * Component for filtering medical history data with various criteria
 */
const HistoryFilters: React.FC<HistoryFiltersProps> = ({
  onFilterChange,
  availableCategories,
  availableProviders,
  className = '',
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [filters, setFilters] = useState<HistoryFilter>({
    timeRange: 'all-time',
    categories: [],
    providers: [],
    severity: [],
    searchTerm: '',
  });

  const timeRangeOptions = [
    { id: 'last-month', label: 'Last Month' },
    { id: 'last-6-months', label: 'Last 6 Months' },
    { id: 'last-year', label: 'Last Year' },
    { id: 'all-time', label: 'All Time' },
  ];

  const severityOptions = [
    { id: 'low', label: 'Low' },
    { id: 'medium', label: 'Medium' },
    { id: 'high', label: 'High' },
  ];

  const handleTimeRangeChange = (timeRange: string) => {
    const newFilters = { ...filters, timeRange };
    setFilters(newFilters);
    onFilterChange(newFilters);
  };

  const handleCategoryToggle = (category: string) => {
    let newCategories: string[];
    
    if (filters.categories.includes(category)) {
      newCategories = filters.categories.filter((c) => c !== category);
    } else {
      newCategories = [...filters.categories, category];
    }
    
    const newFilters = { ...filters, categories: newCategories };
    setFilters(newFilters);
    onFilterChange(newFilters);
  };

  const handleProviderToggle = (provider: string) => {
    let newProviders: string[];
    
    if (filters.providers.includes(provider)) {
      newProviders = filters.providers.filter((p) => p !== provider);
    } else {
      newProviders = [...filters.providers, provider];
    }
    
    const newFilters = { ...filters, providers: newProviders };
    setFilters(newFilters);
    onFilterChange(newFilters);
  };

  const handleSeverityToggle = (severity: string) => {
    let newSeverity: string[];
    
    if (filters.severity.includes(severity)) {
      newSeverity = filters.severity.filter((s) => s !== severity);
    } else {
      newSeverity = [...filters.severity, severity];
    }
    
    const newFilters = { ...filters, severity: newSeverity };
    setFilters(newFilters);
    onFilterChange(newFilters);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const searchTerm = event.target.value;
    const newFilters = { ...filters, searchTerm };
    setFilters(newFilters);
    onFilterChange(newFilters);
  };

  const handleClearFilters = () => {
    const newFilters: HistoryFilter = {
      timeRange: 'all-time',
      categories: [],
      providers: [],
      severity: [],
      searchTerm: '',
    };
    setFilters(newFilters);
    onFilterChange(newFilters);
  };

  const hasActiveFilters = () => {
    return (
      filters.timeRange !== 'all-time' ||
      filters.categories.length > 0 ||
      filters.providers.length > 0 ||
      filters.severity.length > 0 ||
      filters.searchTerm.length > 0
    );
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (filters.timeRange !== 'all-time') count += 1;
    count += filters.categories.length;
    count += filters.providers.length;
    count += filters.severity.length;
    if (filters.searchTerm) count += 1;
    return count;
  };

  return (
    <div className={`bg-[var(--background-elevated)] border border-[var(--border-subtle)] rounded-lg shadow-sm ${className}`}>
      {/* Filter header with expand/collapse toggle */}
      <div 
        className="px-4 py-3 flex items-center justify-between cursor-pointer border-b border-[var(--border-subtle)]"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center">
          <Filter className="w-5 h-5 text-[var(--text-secondary)] mr-2" />
          <h3 className="font-medium text-[var(--text-primary)]">
            Filters
            {getActiveFilterCount() > 0 && (
              <span className="ml-2 px-2 py-0.5 text-xs bg-[var(--brand-primary)] text-white rounded-full">
                {getActiveFilterCount()}
              </span>
            )}
          </h3>
        </div>
        
        <div className="flex items-center">
          {hasActiveFilters() && (
            <button
              className="text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)] mr-3"
              onClick={(e) => {
                e.stopPropagation();
                handleClearFilters();
              }}
            >
              Clear
            </button>
          )}
          
          <svg 
            className={`w-5 h-5 text-[var(--text-secondary)] transition-transform ${isExpanded ? 'transform rotate-180' : ''}`}
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>
      
      {/* Filter content (expanded) */}
      {isExpanded && (
        <div className="p-4">
          {/* Search input */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">
              Search
            </label>
            <input
              type="text"
              placeholder="Search medical history..."
              className="w-full px-3 py-2 border border-[var(--border-subtle)] rounded-md bg-[var(--background-surface)] text-[var(--text-primary)]"
              value={filters.searchTerm}
              onChange={handleSearchChange}
            />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Time range filter */}
            <div>
              <label className="flex items-center text-sm font-medium text-[var(--text-secondary)] mb-2">
                <Calendar className="w-4 h-4 mr-1" />
                Time Period
              </label>
              <div className="space-y-1">
                {timeRangeOptions.map((option) => (
                  <div key={option.id} className="flex items-center">
                    <input
                      type="radio"
                      id={`time-${option.id}`}
                      name="timeRange"
                      className="w-4 h-4 text-[var(--brand-primary)] border-[var(--border-subtle)] focus:ring-[var(--brand-primary)]"
                      checked={filters.timeRange === option.id}
                      onChange={() => handleTimeRangeChange(option.id)}
                    />
                    <label
                      htmlFor={`time-${option.id}`}
                      className="ml-2 text-sm text-[var(--text-secondary)]"
                    >
                      {option.label}
                    </label>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Categories filter */}
            <div>
              <label className="flex items-center text-sm font-medium text-[var(--text-secondary)] mb-2">
                <Tag className="w-4 h-4 mr-1" />
                Categories
              </label>
              <div className="space-y-1 max-h-32 overflow-y-auto pr-1">
                {availableCategories.map((category) => (
                  <div key={category.id} className="flex items-center">
                    <input
                      type="checkbox"
                      id={`category-${category.id}`}
                      className="w-4 h-4 text-[var(--brand-primary)] rounded border-[var(--border-subtle)] focus:ring-[var(--brand-primary)]"
                      checked={filters.categories.includes(category.id)}
                      onChange={() => handleCategoryToggle(category.id)}
                    />
                    <label
                      htmlFor={`category-${category.id}`}
                      className="ml-2 text-sm text-[var(--text-secondary)]"
                    >
                      {category.label}
                    </label>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Providers filter */}
            <div>
              <label className="flex items-center text-sm font-medium text-[var(--text-secondary)] mb-2">
                <User className="w-4 h-4 mr-1" />
                Providers
              </label>
              <div className="space-y-1 max-h-32 overflow-y-auto pr-1">
                {availableProviders.map((provider) => (
                  <div key={provider.id} className="flex items-center">
                    <input
                      type="checkbox"
                      id={`provider-${provider.id}`}
                      className="w-4 h-4 text-[var(--brand-primary)] rounded border-[var(--border-subtle)] focus:ring-[var(--brand-primary)]"
                      checked={filters.providers.includes(provider.id)}
                      onChange={() => handleProviderToggle(provider.id)}
                    />
                    <label
                      htmlFor={`provider-${provider.id}`}
                      className="ml-2 text-sm text-[var(--text-secondary)]"
                    >
                      {provider.name}
                    </label>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Severity filter */}
            <div>
              <label className="flex items-center text-sm font-medium text-[var(--text-secondary)] mb-2">
                <AlertCircle className="w-4 h-4 mr-1" />
                Severity
              </label>
              <div className="space-y-1">
                {severityOptions.map((option) => (
                  <div key={option.id} className="flex items-center">
                    <input
                      type="checkbox"
                      id={`severity-${option.id}`}
                      className="w-4 h-4 text-[var(--brand-primary)] rounded border-[var(--border-subtle)] focus:ring-[var(--brand-primary)]"
                      checked={filters.severity.includes(option.id)}
                      onChange={() => handleSeverityToggle(option.id)}
                    />
                    <label
                      htmlFor={`severity-${option.id}`}
                      className="ml-2 text-sm text-[var(--text-secondary)]"
                    >
                      {option.label}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          {/* Apply filters button */}
          <div className="mt-4 flex justify-end">
            <button
              className="bg-[var(--brand-primary)] text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-[var(--brand-primary-hover)] transition-colors"
              onClick={() => setIsExpanded(false)}
            >
              Apply Filters
            </button>
          </div>
        </div>
      )}
      
      {/* Active filters display (when collapsed) */}
      {!isExpanded && hasActiveFilters() && (
        <div className="p-3 flex flex-wrap gap-2">
          {filters.timeRange !== 'all-time' && (
            <div className="inline-flex items-center bg-[var(--background-subtle)] text-[var(--text-secondary)] text-xs rounded-full px-3 py-1">
              <Calendar className="w-3 h-3 mr-1" />
              {timeRangeOptions.find((o) => o.id === filters.timeRange)?.label}
              <button
                className="ml-1 text-[var(--text-tertiary)] hover:text-[var(--text-secondary)]"
                onClick={() => handleTimeRangeChange('all-time')}
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          )}
          
          {filters.categories.map((category) => (
            <div key={category} className="inline-flex items-center bg-[var(--background-subtle)] text-[var(--text-secondary)] text-xs rounded-full px-3 py-1">
              <Tag className="w-3 h-3 mr-1" />
              {availableCategories.find((c) => c.id === category)?.label}
              <button
                className="ml-1 text-[var(--text-tertiary)] hover:text-[var(--text-secondary)]"
                onClick={() => handleCategoryToggle(category)}
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
          
          {filters.providers.map((provider) => (
            <div key={provider} className="inline-flex items-center bg-[var(--background-subtle)] text-[var(--text-secondary)] text-xs rounded-full px-3 py-1">
              <User className="w-3 h-3 mr-1" />
              {availableProviders.find((p) => p.id === provider)?.name}
              <button
                className="ml-1 text-[var(--text-tertiary)] hover:text-[var(--text-secondary)]"
                onClick={() => handleProviderToggle(provider)}
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
          
          {filters.severity.map((severity) => (
            <div key={severity} className="inline-flex items-center bg-[var(--background-subtle)] text-[var(--text-secondary)] text-xs rounded-full px-3 py-1">
              <AlertCircle className="w-3 h-3 mr-1" />
              {severityOptions.find((s) => s.id === severity)?.label}
              <button
                className="ml-1 text-[var(--text-tertiary)] hover:text-[var(--text-secondary)]"
                onClick={() => handleSeverityToggle(severity)}
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
          
          {filters.searchTerm && (
            <div className="inline-flex items-center bg-[var(--background-subtle)] text-[var(--text-secondary)] text-xs rounded-full px-3 py-1">
              <Search className="w-3 h-3 mr-1" />
              Search: {filters.searchTerm.length > 20 ? `${filters.searchTerm.substring(0, 20)}...` : filters.searchTerm}
              <button
                className="ml-1 text-[var(--text-tertiary)] hover:text-[var(--text-secondary)]"
                onClick={() => {
                  const newFilters = { ...filters, searchTerm: '' };
                  setFilters(newFilters);
                  onFilterChange(newFilters);
                }}
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default HistoryFilters; 